
import React from "react";
import { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// Define a component that wraps the shadcn/ui Card with consistent animations
interface EnhancedCardProps extends React.ComponentProps<typeof Card> {
  animate?: boolean;
  hoverEffect?: boolean;
  variant?: "default" | "glass" | "outline" | "highlight";
}

const EnhancedCard = React.forwardRef<
  HTMLDivElement,
  EnhancedCardProps
>(({ 
  className, 
  animate = true, 
  hoverEffect = true, 
  variant = "default",
  children,
  ...props 
}, ref) => {
  const baseStyles = "transition-all duration-300";
  
  const variantStyles = {
    default: "border bg-card shadow-sm",
    glass: "border border-primary/20 bg-card/80 backdrop-blur-sm",
    outline: "border-2 border-primary/20 bg-card",
    highlight: "border-primary/20 bg-primary/5 shadow-md"
  };
  
  const animationStyles = animate ? "animate-fade-in" : "";
  const hoverStyles = hoverEffect ? "hover:shadow-md hover:-translate-y-1" : "";
  
  return (
    <Card
      ref={ref}
      className={cn(
        baseStyles,
        variantStyles[variant],
        animationStyles,
        hoverStyles,
        className
      )}
      {...props}
    >
      {children}
    </Card>
  );
});
EnhancedCard.displayName = "EnhancedCard";

// Enhanced versions of all Card subcomponents
const EnhancedCardHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CardHeader>
>(({ className, ...props }, ref) => (
  <CardHeader 
    ref={ref} 
    className={cn("pb-2", className)} 
    {...props} 
  />
));
EnhancedCardHeader.displayName = "EnhancedCardHeader";

const EnhancedCardFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CardFooter>
>(({ className, ...props }, ref) => (
  <CardFooter 
    ref={ref} 
    className={cn("flex items-center pt-2", className)} 
    {...props} 
  />
));
EnhancedCardFooter.displayName = "EnhancedCardFooter";

const EnhancedCardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentProps<typeof CardTitle>
>(({ className, ...props }, ref) => (
  <CardTitle 
    ref={ref} 
    className={cn("text-xl font-semibold", className)} 
    {...props} 
  />
));
EnhancedCardTitle.displayName = "EnhancedCardTitle";

const EnhancedCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentProps<typeof CardDescription>
>(({ className, ...props }, ref) => (
  <CardDescription 
    ref={ref} 
    className={cn("text-sm text-muted-foreground", className)} 
    {...props} 
  />
));
EnhancedCardDescription.displayName = "EnhancedCardDescription";

const EnhancedCardContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CardContent>
>(({ className, ...props }, ref) => (
  <CardContent 
    ref={ref} 
    className={cn("pt-2", className)} 
    {...props} 
  />
));
EnhancedCardContent.displayName = "EnhancedCardContent";

export {
  EnhancedCard,
  EnhancedCardHeader,
  EnhancedCardFooter,
  EnhancedCardTitle,
  EnhancedCardDescription,
  EnhancedCardContent
};
